import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { Loader2, Inbox, Trash2, MessageSquare, Phone, Mail, MapPin, Calendar, Filter } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { ADMIN } from "@/constants/testIds";

const STATUSES = ["new", "contacted", "qualified", "quoted", "won", "lost", "closed"];
const STATUS_COLOR = {
  new: "bg-gold/20 text-gold",
  contacted: "bg-navy/30 text-navy",
  qualified: "bg-emerald_brand/20 text-emerald-500",
  quoted: "bg-secondary text-foreground/80",
  won: "bg-emerald_brand/40 text-emerald-500",
  lost: "bg-destructive/20 text-destructive",
  closed: "bg-secondary text-foreground/60",
};

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState("new");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (typeFilter !== "all") params.type = typeFilter;
      if (statusFilter !== "all") params.status = statusFilter;
      const { data } = await api.get("/admin/leads", { params });
      setLeads(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [typeFilter, statusFilter]);
  useEffect(() => { load(); }, [load]);

  const openLead = (l) => {
    setSelected(l);
    setNotes(l.notes || "");
    setStatus(l.status || "new");
  };

  const saveLead = async () => {
    try {
      await api.put(`/admin/leads/${selected.id}`, { status, notes });
      toast.success("Lead updated.");
      setSelected(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/admin/leads/${deleteTarget.id}`);
      toast.success("Lead deleted.");
      setDeleteTarget(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const leadRows = useMemo(
    () => leads.map((l) => ({
      ...l,
      _location: [l.city, l.state].filter(Boolean).join(", "),
      _createdAt: new Date(l.created_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }),
      _typeInitial: (l.type?.[0] || "L").toUpperCase(),
    })),
    [leads],
  );

  return (
    <div data-testid={ADMIN.leadsList} className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="section-label mb-1">Business Enquiries</p>
          <h1 className="serif-display text-3xl md:text-4xl text-foreground">Leads</h1>
          <p className="mt-2 text-sm text-foreground/70 max-w-2xl">Catalogue requests, quotation requests, and contact submissions.</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="h-10 w-40 rounded-sm bg-card"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="catalogue">Catalogue</SelectItem>
              <SelectItem value="quotation">Quotation</SelectItem>
              <SelectItem value="contact">Contact</SelectItem>
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-10 w-40 rounded-sm bg-card"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <LeadsListBody loading={loading} leadRows={leadRows} openLead={openLead} />

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Lead Details</DialogTitle>
          </DialogHeader>
          {selected && (
            <div className="space-y-4 pt-2">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <Field label="Type" value={selected.type} />
                <Field label="Company" value={selected.company_name} />
                <Field label="Contact Person" value={selected.contact_person} />
                <Field label="Phone" value={selected.phone} />
                <Field label="Email" value={selected.email} />
                <Field label="GST" value={selected.gst} />
                <Field label="Business Type" value={selected.business_type} />
                <Field label="Monthly Purchase" value={selected.monthly_purchase} />
                <Field label="State" value={selected.state} />
                <Field label="City" value={selected.city} />
                <Field label="Received" value={new Date(selected.created_at).toLocaleString("en-IN")} />
              </div>
              {selected.message && (
                <div>
                  <p className="text-xs tracking-widest uppercase text-foreground/60 mb-1">Message</p>
                  <div className="p-3 rounded-sm bg-secondary text-sm text-foreground/80 whitespace-pre-wrap">{selected.message}</div>
                </div>
              )}
              <div>
                <Label>Update Status</Label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger className="mt-2 rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Internal Notes</Label>
                <Textarea className="mt-2 rounded-sm min-h-[100px]" value={notes} onChange={(e) => setNotes(e.target.value)} />
              </div>
              <div className="flex flex-wrap gap-2 pt-2">
                <a href={`tel:${selected.phone}`} className="inline-flex items-center gap-2 h-10 px-4 rounded-sm border border-border hover:border-gold text-xs tracking-wider uppercase">
                  <Phone size={13} /> Call
                </a>
                <a href={`https://wa.me/${selected.phone.replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 h-10 px-4 rounded-sm border border-emerald_brand text-emerald-500 hover:bg-emerald_brand/10 text-xs tracking-wider uppercase">
                  <MessageSquare size={13} /> WhatsApp
                </a>
                {selected.email && (
                  <a href={`mailto:${selected.email}`} className="inline-flex items-center gap-2 h-10 px-4 rounded-sm border border-border hover:border-gold text-xs tracking-wider uppercase">
                    <Mail size={13} /> Email
                  </a>
                )}
                <div className="ml-auto flex gap-2">
                  <Button variant="outline" onClick={() => setDeleteTarget(selected)} className="rounded-sm border-destructive/60 text-destructive hover:bg-destructive/10">
                    <Trash2 size={13} className="mr-2" /> Delete
                  </Button>
                  <Button onClick={saveLead} className="rounded-sm bg-gold hover:bg-gold-400 text-navy-900 font-semibold">Save Changes</Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this lead?</AlertDialogTitle>
            <AlertDialogDescription>This will permanently remove the enquiry.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-destructive hover:bg-destructive/90 text-destructive-foreground">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Field({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <p className="text-xs tracking-widest uppercase text-foreground/60">{label}</p>
      <p className="mt-0.5 text-foreground text-sm break-words">{value}</p>
    </div>
  );
}

function LeadsListBody({ loading, leadRows, openLead }) {
  if (loading) {
    return (
      <div className="py-24 text-center text-xs tracking-widest uppercase text-foreground/60">
        <Loader2 size={16} className="inline animate-spin mr-2" />Loading…
      </div>
    );
  }
  if (leadRows.length === 0) {
    return (
      <div className="py-24 text-center border border-dashed border-border rounded-md">
        <Inbox size={30} className="mx-auto text-foreground/40 mb-4" />
        <p className="font-serif text-2xl">No leads to show.</p>
        <p className="mt-2 text-sm text-foreground/60">Business enquiries will appear here.</p>
      </div>
    );
  }
  return (
    <div className="grid gap-2">
      {leadRows.map((l) => (
        <LeadRow key={l.id} lead={l} onSelect={openLead} />
      ))}
    </div>
  );
}

function LeadRow({ lead, onSelect }) {
  const badgeClass = STATUS_COLOR[lead.status] || "bg-secondary";
  const statusPillClass = STATUS_COLOR[lead.status] || "bg-secondary text-foreground/70";
  return (
    <button
      onClick={() => onSelect(lead)}
      className="p-4 rounded-md border border-border/60 bg-card hover:border-gold/60 transition-colors text-left"
    >
      <div className="flex items-start gap-4">
        <div className={`h-9 w-9 rounded-sm flex items-center justify-center shrink-0 text-xs font-semibold uppercase ${badgeClass}`}>
          {lead._typeInitial}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-baseline gap-3">
            <p className="font-serif text-lg text-foreground">{lead.contact_person || "Unnamed"}</p>
            <span className={`text-[0.6rem] tracking-widest uppercase px-2 py-0.5 rounded-sm ${statusPillClass}`}>{lead.status}</span>
            <span className="text-[0.6rem] tracking-widest uppercase text-foreground/60">{lead.type}</span>
          </div>
          <div className="mt-1 text-sm text-foreground/70">{lead.company_name || "—"}</div>
          <div className="mt-2 flex flex-wrap gap-4 text-xs text-foreground/60">
            <span className="flex items-center gap-1"><Phone size={11} /> {lead.phone}</span>
            {lead.email && <span className="flex items-center gap-1"><Mail size={11} /> {lead.email}</span>}
            {lead._location && <span className="flex items-center gap-1"><MapPin size={11} /> {lead._location}</span>}
            <span className="flex items-center gap-1 ml-auto"><Calendar size={11} /> {lead._createdAt}</span>
          </div>
        </div>
      </div>
    </button>
  );
}
